// Reference-audit orchestrator.
//
// For each ReferenceEntry with a DOI: resolve via Crossref. Compare resolved
// metadata to the extracted reference; flag retraction, year mismatch, or
// author mismatch.
//
// Performance: Crossref's polite pool tolerates ~50 req/s but a typical
// manuscript has 20–60 references. We rate-limit to 6 concurrent requests
// via a small in-process semaphore — fast enough to feel instant, slow enough
// not to anger Crossref.

import { resolveDoi } from './crossref';
import type { ReferenceEntry } from '../patterns/types';
import type {
  CrossrefAuthor,
  CrossrefWork,
  ReferenceAuditEntry,
  ReferenceAuditOutcome,
  ReferenceAuditResult,
} from './types';

const CONCURRENCY = 6;

function detectMismatches(ref: ReferenceEntry, work: CrossrefWork): string[] {
  const out: string[] = [];
  if (ref.year !== null && work.year !== null && Math.abs(ref.year - work.year) > 1) {
    out.push(`year mismatch: reference says ${ref.year}, Crossref says ${work.year}`);
  }
  if (ref.authors.length > 0 && work.authors.length > 0) {
    if (!firstAuthorMatches(ref.authors[0], work.authors)) {
      out.push(`first-author mismatch: reference lists "${ref.authors[0]}", Crossref lists "${authorLabel(work.authors[0])}"`);
    }
  }
  return out;
}

function authorLabel(a: CrossrefAuthor): string {
  if (a.family && a.given) return `${a.family} ${a.given[0]}`;
  return a.family ?? a.given ?? '';
}

function firstAuthorMatches(refFirst: string, workAuthors: CrossrefAuthor[]): boolean {
  // Take just the surname for comparison; reference parsing often produces "Smith J".
  const refSurname = refFirst.split(/\s+/)[0]?.toLowerCase() ?? '';
  if (!refSurname) return true;
  for (const a of workAuthors.slice(0, 5)) {
    if (a.family && a.family.toLowerCase() === refSurname) return true;
  }
  return false;
}

async function auditOne(
  ref: ReferenceEntry,
  mailto: string | undefined,
): Promise<ReferenceAuditOutcome> {
  if (!ref.doi) return { status: 'unresolved', reason: 'no_doi' };
  const res = await resolveDoi(ref.doi, { mailto });
  if (!res.ok) {
    if (res.error.kind === 'not_found') {
      return { status: 'unresolved', reason: 'doi_not_found' };
    }
    return { status: 'error', error: res.error };
  }
  const work = res.data;
  if (work.isRetracted) return { status: 'retracted', work };
  const mismatches = detectMismatches(ref, work);
  if (mismatches.length > 0) return { status: 'mismatch', work, mismatches };
  return { status: 'resolved', work };
}

export interface ReferenceAuditOptions {
  mailto?: string;
}

async function runWithConcurrency<I, O>(
  inputs: I[],
  concurrency: number,
  worker: (input: I) => Promise<O>,
): Promise<O[]> {
  const results: O[] = new Array(inputs.length);
  let next = 0;
  async function drain(): Promise<void> {
    let i = next++;
    while (i < inputs.length) {
      results[i] = await worker(inputs[i]);
      i = next++;
    }
  }
  const runners = Array.from({ length: Math.min(concurrency, inputs.length) }, () => drain());
  await Promise.all(runners);
  return results;
}

export async function auditReferences(
  refs: ReferenceEntry[],
  opts: ReferenceAuditOptions = {},
): Promise<ReferenceAuditResult> {
  const outcomes = await runWithConcurrency(refs, CONCURRENCY, (r) => auditOne(r, opts.mailto));
  const entries: ReferenceAuditEntry[] = refs.map((r, i) => ({
    referenceNumber: r.number,
    doi: r.doi,
    rawTitle: r.title,
    rawYear: r.year,
    outcome: outcomes[i],
  }));
  const degraded = outcomes.some((o) => o.status === 'error');
  return { entries, degraded };
}
